import { computed, type Ref, watchEffect } from "vue";
import { type RouteLocationRaw, useRoute, useRouter } from "vue-router";
import { hasConfigurableRegistrationEnabled } from "@/composables/authorization.js";
import { useGetOneOrder } from "@/providers/orders/useGetOneOrder";
import { RegistrationStage } from "../constants";
import { getSingleParam } from "../utils/utils";

interface RegistrationOptionsReturn {
  isLegacy: Ref<boolean>;
  hasLegacyOrderId: Ref<boolean>;
  isLegacyOrderLoading: Ref<boolean>;
  programPageId: Ref<string | undefined>;
  sessionId: Ref<string | undefined>;
  applicationId: Ref<string | undefined>;
}

export const useRegistrationOptions = (): RegistrationOptionsReturn => {
  const route = useRoute();
  const router = useRouter();

  const isLegacyPath = computed<boolean>(() => {
    const routeName: string = route.name?.toString() ?? "";
    return ["personalize", "configurator-new", "configurator"].includes(routeName?.toString());
  });
  const legacyOrderId = computed<string | undefined>(() => getSingleParam(route.params?.orderId));

  const hasLegacyOrderId = computed(() => Boolean(legacyOrderId.value));

  const { data: legacyOrder, isLoading: isLegacyOrderLoading } = useGetOneOrder(legacyOrderId);

  const programPageId = computed<string | undefined>(() => getSingleParam(route.params.programPageId));

  const legacySlug = computed<string | undefined>(() => getSingleParam(route.params.slug));

  const applicationId = computed<string | undefined>(() => {
    if (!isLegacyPath.value) {
      return getSingleParam(route.params?.applicationId);
    } else if (route.name === "configurator") {
      return legacyOrder.value?.application_id;
    }
    return undefined;
  });

  const sessionId = computed<string | undefined>(() => {
    if (!isLegacyPath.value) {
      return getSingleParam(route.params?.sessionId);
    } else if (route.name === "configurator-new") {
      return route.query["session"]?.toString();
    }
    return legacyOrder.value?.session_id;
  });

  const isLegacy = computed<boolean>(() => {
    if (!isLegacyPath.value) {
      return false;
    }
    if (hasConfigurableRegistrationEnabled.value) {
      return false;
    }
    return true;
  });

  watchEffect(() => {
    if (hasConfigurableRegistrationEnabled.value && isLegacyPath.value && !isLegacyOrderLoading.value) {
      let routeTo: RouteLocationRaw = {};
      switch (route.name?.toString()) {
        case "personalize": {
          routeTo = {
            name: "results",
          };
          break;
        }
        case "configurator-new": {
          routeTo = {
            name: "new-application",
            params: {
              programPageId: programPageId.value,
              sessionId: sessionId.value,
              stage: RegistrationStage.PERSONALIZE,
              step: legacySlug.value,
            },
            query: { ...route.query, session: undefined },
          };
          break;
        }
        case "configurator": {
          routeTo = {
            name: "modify-application",
            params: {
              applicationId: applicationId.value,
              stage: RegistrationStage.APPLY,
              step: legacySlug.value,
            },
            query: { ...route.query },
          };
        }
      }
      router.replace(routeTo);
    }
  });

  return {
    isLegacy,
    hasLegacyOrderId,
    isLegacyOrderLoading,
    programPageId,
    sessionId,
    applicationId,
  };
};
